import { useState } from 'react'
import {
  Box,
  Typography,
  Link,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { directTreeAccessUrl } from 'infrastructure/api/kiddomPaths'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import { StartAndMonitorFlow } from '../../contentService/FlowStartandMonitor'
import { PollingFlowStatus } from '../../contentService/PollingFlowStatus'
import {
  canStartPrepareAtom,
  prepareResultAtom,
  selectedLessonIdsAtom,
} from './atoms'
import {
  useExistingPrepareFlowId,
  usePrepareStartFlow,
  usePrepareFlowComplete,
  useExistingFlowPoll,
  useResetPrepareStage,
  useResetProcessStage,
} from './hooks'
import { ResetStage } from './ResetStage'
import type { StageProps } from './types'

export function PrepareStage({ treeId, versionId }: StageProps) {
  const canStart = useAtomValue(canStartPrepareAtom)
  const prepareResult = useAtomValue(prepareResultAtom)
  const selectedLessonIds = useAtomValue(selectedLessonIdsAtom)
  const setSelectedLessonIds = useSetAtom(selectedLessonIdsAtom)
  const { existingFlowId, isChecking, clearExistingFlowId } =
    useExistingPrepareFlowId(treeId, versionId)
  const startFlowFunction = usePrepareStartFlow(treeId, versionId)
  const { handleFlowComplete, error, clearError } = usePrepareFlowComplete(
    treeId,
    versionId,
  )
  const onExistingFlowPoll = useExistingFlowPoll(
    handleFlowComplete,
    clearExistingFlowId,
  )
  const { resetProcessStage } = useResetProcessStage(treeId, versionId)
  const { resetPrepareStage, isResetting } = useResetPrepareStage(
    treeId,
    versionId,
    resetProcessStage,
  )
  const [prepareFlowResetTrigger, setPrepareFlowResetTrigger] = useState(1)

  return (
    <Box border={1} borderColor="grey.300" borderRadius={1} p={2} mb={2}>
      <Typography variant="h6" gutterBottom sx={{ fontStyle: 'italic' }}>
        Finding Lessons
      </Typography>

      {!isChecking && existingFlowId && (
        <PollingFlowStatus
          flowId={existingFlowId}
          onFlowPoll={onExistingFlowPoll}
          pollingIntervalMs={2000}
        />
      )}

      {!isChecking && !existingFlowId && (
        <StartAndMonitorFlow
          disabled={!canStart}
          buttonText="Generate Slides"
          processingText="Loading lessons..."
          startFlowFunction={startFlowFunction}
          onFlowComplete={handleFlowComplete}
          pollingIntervalMs={2000}
          resetTrigger={prepareFlowResetTrigger}
        />
      )}

      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}

      {prepareResult && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Drive folder:{' '}
            <Link
              href={prepareResult.directory_link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {prepareResult.directory_name}
            </Link>
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Lessons ({selectedLessonIds.size}/{prepareResult.lessons.length}{' '}
            selected)
          </Typography>
          <Box
            sx={{
              maxHeight: 160,
              overflowY: 'auto',
              border: 1,
              borderColor: 'grey.300',
              borderRadius: 1,
              px: 1,
            }}
          >
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      selectedLessonIds.size === prepareResult.lessons.length
                    }
                    indeterminate={
                      selectedLessonIds.size > 0 &&
                      selectedLessonIds.size < prepareResult.lessons.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedLessonIds(
                          new Set(
                            prepareResult.lessons.map((l) => l.lesson_id),
                          ),
                        )
                      } else {
                        setSelectedLessonIds(new Set())
                      }
                    }}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight="bold">
                    Select All
                  </Typography>
                }
                sx={{ borderBottom: 1, borderColor: 'grey.200', mb: 0 }}
              />
              {[...prepareResult.lessons]
                .sort((a, b) => a.lesson_title.localeCompare(b.lesson_title))
                .map((lesson) => (
                  <FormControlLabel
                    key={lesson.lesson_id}
                    control={
                      <Checkbox
                        checked={selectedLessonIds.has(lesson.lesson_id)}
                        onChange={(e) => {
                          const next = new Set(selectedLessonIds)
                          if (e.target.checked) {
                            next.add(lesson.lesson_id)
                          } else {
                            next.delete(lesson.lesson_id)
                          }
                          setSelectedLessonIds(next)
                        }}
                        size="small"
                      />
                    }
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="body2">
                          {lesson.lesson_title}
                        </Typography>
                        <IconButton
                          component="a"
                          href={directTreeAccessUrl({
                            treeId,
                            versionId,
                            nodeId: lesson.lesson_id,
                          })}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          sx={{ p: 0.25 }}
                        >
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    }
                    sx={{ ml: 1, my: -0.5 }}
                  />
                ))}
            </FormGroup>
          </Box>
        </Box>
      )}

      {(prepareResult || error || existingFlowId) && (
        <Box sx={{ mt: 1 }}>
          <ResetStage
            reset={async () => {
              clearExistingFlowId()
              clearError()
              await resetPrepareStage()
              setPrepareFlowResetTrigger(0)
              setTimeout(() => setPrepareFlowResetTrigger(1), 0)
            }}
            isResetting={isResetting}
            buttonText="Reset stage"
            dialogTitle="Reset prepare stage?"
            dialogContentText="This will clear the lesson list and any prepare flow."
          />
        </Box>
      )}
    </Box>
  )
}
